import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { importDraftDocument } from '../services/api';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, FileJson, Loader2, X } from 'lucide-react';

/**
 * Import a finished document — a New Design (Statement of Work) or a Change
 * Request / Design Proposal — saved as a DocHub draft file (.json).
 *
 * The file is sent as it is; only the project picked here (and the title, if
 * edited) are set on it. The server checks it against the same house rules as a
 * generated draft and either refuses it with a list of what to fix, or files it
 * as a normal document and returns any softer warnings. On success the page opens
 * the normal draft page, so the document is reviewed exactly like a generated one.
 */

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const KIND_LABELS = {
  sow: 'New Design (Statement of Work)',
  change_request: 'Change Request',
  design: 'High-Level Design Proposal',
};

const kindLabel = (docKind, docType) => {
  if (docKind === 'sow') return KIND_LABELS.sow;
  return KIND_LABELS[docType] || 'Change Request / Design Proposal';
};

const draftPath = (docKind, id) => (docKind === 'sow' ? `/sow-builder/${id}` : `/design-builder/${id}`);

const REFUSED_HEADING = 'DocHub did not import this document. Fix the following in the file, then import it again:';

// FastAPI's own validation errors (a body it cannot parse) arrive as [{loc: ['body', ...], msg}].
const describeValidationError = (error) => {
  const where = (Array.isArray(error?.loc) ? error.loc : []).filter((part) => part !== 'body').join(' › ');
  const message = error?.msg === 'Field required' ? 'is missing' : (error?.msg || '');
  return where ? `${where}: ${message}` : message;
};

// Turn a failed request into a heading plus the list of things to fix. A refusal
// (422) is detail = {message, problems, warnings}; a missing project (404) is a sentence.
const describeFailure = (error) => {
  const res = error?.response;
  if (!res) {
    return { heading: 'Could not reach DocHub. Check your connection and try again.', items: [], warnings: [] };
  }
  const detail = res.data?.detail;
  const sentence = typeof detail === 'string' ? detail : '';

  if (res.status === 401 || res.status === 403) {
    return { heading: 'Only editors and admins can import documents.', items: [], warnings: [] };
  }
  if (res.status === 404) {
    return { heading: sentence || 'That project no longer exists. Pick another project and try again.', items: [], warnings: [] };
  }
  if (res.status === 413) {
    return { heading: 'The file is too large for DocHub to accept.', items: [], warnings: [] };
  }
  if (res.status === 400 || res.status === 422) {
    if (Array.isArray(detail)) {
      return { heading: REFUSED_HEADING, items: detail.map(describeValidationError), warnings: [] };
    }
    if (detail && typeof detail === 'object') {
      return { heading: detail.message || REFUSED_HEADING, items: detail.problems || [], warnings: detail.warnings || [] };
    }
    return { heading: sentence || REFUSED_HEADING, items: [], warnings: [] };
  }
  return {
    heading: `DocHub hit an error while importing (HTTP ${res.status}). Look for the document under Documentation before trying again.`,
    items: sentence ? [sentence] : [],
    warnings: [],
  };
};

const MessageList = ({ items, className }) => (
  <ul className={`list-disc pl-5 space-y-1 ${className || ''}`}>
    {items.map((line, i) => <li key={i}>{line}</li>)}
  </ul>
);

const ImportDocumentPanel = ({ customers, projects, initialCustomer = '', initialProject = '', onCancel }) => {
  const navigate = useNavigate();
  const fileInput = useRef(null);

  const [customerId, setCustomerId] = useState(initialCustomer);
  const [projectId, setProjectId] = useState(initialProject);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState('');
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [imported, setImported] = useState(null);

  const filteredProjects = customerId ? projects.filter((p) => p.customer_id === customerId) : projects;
  const projectName = (id) => projects.find((p) => p.id === id)?.name;

  const clearOutcome = () => { setRefusal(null); setImported(null); };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // picking the same (fixed) file again must fire again
    if (!file) return;
    clearOutcome();
    setParsed(null);
    setParseError('');
    setFileName(file.name);

    if (file.size > MAX_FILE_BYTES) {
      setParseError(`This file is ${(file.size / 1024 / 1024).toFixed(1)} MB. A document draft is normally well under 1 MB — check you picked the right file.`);
      return;
    }
    let doc;
    try {
      doc = JSON.parse(await file.text());
    } catch (err) {
      setParseError(`This file is not valid JSON, so DocHub cannot read it. The reader stopped with: ${err.message}`);
      return;
    }
    if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
      setParseError('This file does not hold a document draft. It should be one JSON object with doc_kind, title and the draft.');
      return;
    }
    const docKind = String(doc.doc_kind || '').trim().toLowerCase();
    if (docKind !== 'sow' && docKind !== 'design') {
      setParseError(doc.doc_kind
        ? `The file says doc_kind "${doc.doc_kind}". It must be "sow" (New Design) or "design" (Change Request / Design Proposal).`
        : 'The file does not say what it is: add doc_kind "sow" (New Design) or "design" (Change Request / Design Proposal).');
      return;
    }

    setParsed({ ...doc, doc_kind: docKind });
    setTitle(typeof doc.title === 'string' ? doc.title : '');
    // A file that names a project this list knows picks it, unless one is already chosen.
    const named = doc.project_id && projects.find((p) => p.id === doc.project_id);
    if (named && !projectId) {
      setCustomerId(named.customer_id);
      setProjectId(named.id);
    }
  };

  const handleImport = async () => {
    if (!parsed || !projectId || submitting) return;
    if (!title.trim()) {
      toast.error('Please enter a title for the document');
      return;
    }
    clearOutcome();
    setSubmitting(true);
    try {
      const res = await importDraftDocument({ ...parsed, project_id: projectId, title: title.trim() });
      const { doc_id: docId, doc_kind: docKind, warnings = [] } = res.data;
      if (warnings.length) {
        setImported({ docId, docKind, warnings });
        toast.success('Document imported, with notes to review');
        return;
      }
      toast.success('Document imported');
      navigate(draftPath(docKind, docId));
    } catch (error) {
      setRefusal(describeFailure(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Only a project this list knows counts: a placeholder id ("REPLACE_WITH_PROJECT_ID") names none.
  const fileProject = parsed?.project_id ? projectName(parsed.project_id) : undefined;
  const fileProjectDiffers = !!fileProject && !!projectId && parsed.project_id !== projectId;
  // Once imported, the form is locked so the same file cannot be filed twice.
  const locked = submitting || !!imported;
  const canImport = !!parsed && !!projectId && !!title.trim() && !locked;

  return (
    <Card className="border-slate-200" data-testid="import-doc-panel">
      <CardHeader>
        <CardTitle className="text-base font-['Plus_Jakarta_Sans']">Import a finished document</CardTitle>
        <p className="text-xs leading-relaxed text-slate-500">
          A New Design (Statement of Work) or a Change Request / Design Proposal saved as a DocHub draft file (.json).
          DocHub checks it against the same house rules as its own drafts, then files it under the project — with
          versions, chat edits and the branded Word and PDF — like any document it writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Customer</Label>
            <Select
              value={customerId}
              onValueChange={(v) => { setCustomerId(v); setProjectId(''); setRefusal(null); }}
              disabled={locked}
            >
              <SelectTrigger data-testid="import-doc-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Project *</Label>
            <Select
              value={projectId}
              onValueChange={(v) => {
                setProjectId(v);
                const p = projects.find((x) => x.id === v);
                if (p && !customerId) setCustomerId(p.customer_id);
                setRefusal(null);
              }}
              disabled={locked}
            >
              <SelectTrigger data-testid="import-doc-project-select">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {filteredProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="import-doc-file">Document file *</Label>
          <div className="flex items-center gap-3">
            <input
              id="import-doc-file"
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFile}
              data-testid="import-doc-file-input"
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={locked}>
              <FileJson className="h-4 w-4 mr-1.5" /> {fileName ? 'Choose another file' : 'Choose .json file'}
            </Button>
            <span className="text-xs text-slate-500 truncate">{fileName || 'No file chosen'}</span>
          </div>
        </div>

        {parseError && (
          <div className="rounded-lg border border-red-200 bg-red-50/70 px-4 py-3 text-xs leading-relaxed text-red-800 flex items-start gap-2" data-testid="import-doc-parse-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />
            <span>{parseError}</span>
          </div>
        )}

        {parsed && (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600" data-testid="import-doc-kind">
              <span className="font-semibold text-slate-700">{kindLabel(parsed.doc_kind, parsed.doc_type)}</span>
              {projectId ? <> — goes into <span className="font-medium text-slate-700">{projectName(projectId) || 'the chosen project'}</span></> : ' — pick the project it belongs to'}
              {fileProjectDiffers && (
                <span className="block mt-1 text-amber-700">
                  The file names a different project ({fileProject}). It will go into the project picked above.
                </span>
              )}
            </div>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={title}
                onChange={(e) => { setTitle(e.target.value); setRefusal(null); }}
                placeholder="The document title, as the customer will see it"
                disabled={locked}
                data-testid="import-doc-title-input"
              />
            </div>
          </div>
        )}

        {refusal && (
          <div className="rounded-lg border border-red-200 bg-red-50/70 px-4 py-3 text-xs leading-relaxed text-red-800 space-y-2" data-testid="import-doc-refusal">
            <p className="flex items-start gap-2 font-semibold">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-600" />
              <span>{refusal.heading}</span>
            </p>
            {refusal.items.length > 0 && <MessageList items={refusal.items} />}
            {refusal.warnings.length > 0 && (
              <div className="text-amber-800">
                <p className="font-semibold">Also worth checking (these alone would not stop the import):</p>
                <MessageList items={refusal.warnings} />
              </div>
            )}
          </div>
        )}

        {imported && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs leading-relaxed text-amber-800 space-y-2" data-testid="import-doc-warnings">
            <p className="flex items-start gap-2 font-semibold text-emerald-800">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
              <span>Imported. DocHub noted the following — worth a look before the document goes out:</span>
            </p>
            <MessageList items={imported.warnings} />
            <Button
              size="sm"
              className="mt-1 bg-[#2B8FD8] hover:bg-[#2075b3]"
              onClick={() => navigate(draftPath(imported.docKind, imported.docId))}
              data-testid="import-doc-open-btn"
            >
              Open the document
            </Button>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={submitting} data-testid="import-doc-cancel-btn">
            <X className="h-4 w-4 mr-1" /> {imported ? 'Close' : 'Cancel'}
          </Button>
          <Button
            className="bg-[#2B8FD8] hover:bg-[#2075b3]"
            onClick={handleImport}
            disabled={!canImport}
            data-testid="import-doc-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileJson className="h-4 w-4 mr-1.5" />}
            {submitting ? 'Checking and importing…' : 'Import document'}
          </Button>
        </div>
        {!canImport && !submitting && !imported && (
          <p className="text-xs text-right text-slate-500 -mt-3">
            {!projectId ? 'Pick the project.' : !parsed ? 'Choose the document file.' : !title.trim() ? 'Enter a title.' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// The message helpers are exported for the tests.
export { describeFailure, kindLabel, draftPath };
export default ImportDocumentPanel;
